import { Plus } from 'lucide-react';
import {
  type MouseEvent,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { CenterFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Menu from '@/components/Menu/Menu';
import useModalState from '@/hooks/pure/useModalState';
import type { FullColumnKey } from '@/types/filterKeyConst';
import type { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import FieldItem from './components/FieldItem';
import FieldItemSelect from './components/FieldItemSelect';
import useStructuredFilterMap from './configs/useStructuredFilterMap';
import { useStructuredFilterMenus } from './hooks/useStructuredFilterMenus';
import structuredFilterUtils from './utils/structuredFilter.utils';

type Props = {
  values: StructuredFilterQueryFormatT[];
  onChange: (values: StructuredFilterQueryFormatT[]) => void;
  triggerNode?: ReactElement;
  clearFilter: () => void;
  suffixNode?: ReactElement;
  disAllowFilter?: FullColumnKey[];
  isMenuOpen?: boolean;
};

const StructuredFilter = (props: Props) => {
  const fieldOptions = useStructuredFilterMap();

  const ref = useRef<HTMLDivElement>(null);
  const [subMenuStatus, setSubMenuStatus] = useState<string | null>(null);

  const {
    open: openFieldItemSelect,
    close: closeFieldItemSelect,
    status: fieldItemSelectStatus,
    modalState: fieldItemSelectState,
    pos: fieldItemSelectPosition,
  } = useModalState<
    StructuredField & {
      valueIndex: number;
    }
  >();

  const {
    open: openMenu,
    close: closeMenu,
    status: menuStatus,
  } = useModalState();

  useEffect(() => {
    if (props.isMenuOpen && !menuStatus) {
      openMenu();
    }
  }, [menuStatus, openMenu, props.isMenuOpen]);

  const menuOptions = useStructuredFilterMenus({
    triggerRef: ref,
    openFieldItemSelect,
    values: props.values,
    disAllowFilter: props.disAllowFilter,
    closeMenu,
    setSubMenuStatus,
    subMenuStatus,
  });

  const onOperatorChange = (
    operator: STRUCTURED_FILTER_OPERATOR,
    index: number,
  ) => {
    const newValues = [...props.values];
    if (newValues[index]) {
      newValues[index] = {
        ...newValues[index],
        operator,
      };
      props.onChange(newValues);
    }
  };

  const onClickValue = (
    e: MouseEvent,
    index: number,
    fieldInfo: StructuredField,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    openFieldItemSelect({
      pos: {
        top: rect ? rect?.top + rect?.height + 8 : 0,
        left: rect ? rect?.left : 0,
      },
      modalState: {
        valueIndex: index,
        ...fieldInfo,
      },
    });
  };

  return (
    <ErrorBoundary
      fallbackRender={() => {
        return <div>Error</div>;
      }}
    >
      <div className="flex flex-wrap gap-2 items-center" ref={ref}>
        <Menu
          modalWidth={180}
          isOpen={props.isMenuOpen ?? menuStatus}
          openMenu={openMenu}
          closeMenu={closeMenu}
          triggerNode={
            props.triggerNode || (
              <Button
                variant="outlined"
                sizeType="m"
                leftIcon={<Plus />}
                rounded
              >
                Filter
              </Button>
            )
          }
          options={menuOptions}
        />

        {props.values.map((filterQuery, index) => {
          return (
            <ErrorBoundary
              key={index}
              fallbackRender={() => {
                return <CenterFlex padding="8px">Field item error</CenterFlex>;
              }}
            >
              {(function () {
                const result = structuredFilterUtils.getFieldInfo(
                  filterQuery.columnKey,
                  fieldOptions,
                );
                const fieldInfo = result?.fieldInfo;
                if (!fieldInfo) return null;
                return (
                  <FieldItem
                    filterIndex={index}
                    columnName={fieldInfo.columnName}
                    isMultiValueField={fieldInfo.isMultiValueField}
                    filterQuery={filterQuery}
                    onClickValue={(e) => onClickValue(e, index, fieldInfo)}
                    onOperatorChange={(operator) =>
                      onOperatorChange(operator, index)
                    }
                    onRemoveClick={() => {
                      props.onChange(
                        props.values.filter((_, i) => i !== index),
                      );
                    }}
                  />
                );
              })()}
            </ErrorBoundary>
          );
        })}
        {props.suffixNode}

        {props.values.length > 0 && (
          <Button variant="ghost" sizeType="s" onClick={props.clearFilter}>
            Clear
          </Button>
        )}

        {fieldItemSelectState && fieldItemSelectStatus && (
          <FieldItemSelect
            initailValues={props.values[fieldItemSelectState.valueIndex]}
            structuredField={fieldItemSelectState}
            status={fieldItemSelectStatus}
            close={closeFieldItemSelect}
            pos={fieldItemSelectPosition}
            onChange={(filterQueryFormat) => {
              let newValues = [...props.values];
              newValues[fieldItemSelectState.valueIndex] = filterQueryFormat;

              if (fieldItemSelectState.filterItemOptions?.type === 'range') {
                if (
                  !filterQueryFormat.rangeValues?.min &&
                  !filterQueryFormat.rangeValues?.max
                ) {
                  newValues = newValues.filter(
                    (_, i) => i !== fieldItemSelectState.valueIndex,
                  );
                }
              } else {
                if (filterQueryFormat.filterValues.length === 0) {
                  newValues = newValues.filter(
                    (_, i) => i !== fieldItemSelectState.valueIndex,
                  );
                }
              }
              props.onChange(newValues);
            }}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default StructuredFilter;
